import React, { useCallback, useMemo, useState } from 'react'
import { IVehicle } from '@cbgms/api'
import VinAlreadyExistsModal from '../VinAlreadyExistsModal'

type DuplicateVinFunc = (newVehicle: IVehicle, oldVehicle: IVehicle) => Promise<void>

interface IContextValue {
  showSelection: DuplicateVinFunc
  hasBeenProvided: boolean
}

const defaultDuplicateVinContext: IContextValue = {
  showSelection: (() => {}) as any,
  hasBeenProvided: false
}

export const DuplicateVinContext = React.createContext<IContextValue>(defaultDuplicateVinContext)

interface State {
  newVehicle?: IVehicle
  oldVehicle?: IVehicle
  resolve?: () => void
  reject?: (reason?: any) => void
}

const initialState = {
  newVehicle: undefined,
  oldVehicle: undefined,
  resolve: undefined,
  reject: undefined
}

// DuplicateVin is a context that will expose a function that triggers a selection modal for users to resolve a duplicate vin
// when calling showSelection, we return a promise that will resolve once a selection is made OR reject when cancelled/closed.
const DuplicateVin: React.FC<{}> = ({ children }) => {
  const [state, setState] = useState<State>(initialState)
  const cancel = useCallback(() => state.reject && state.reject(), [state.reject])
  const showSelection = useCallback<DuplicateVinFunc>(
    (newVehicle, oldVehicle): Promise<void> => {
      if (state.reject !== undefined) {
        state.reject()
      }
      return new Promise((resolve, reject) => {
        setState({
          newVehicle,
          oldVehicle,
          resolve: () => {
            setState(initialState)
            resolve()
          },
          reject: () => {
            setState(initialState)
            reject('no-selection-made')
          }
        })
      })
    },
    [state.reject]
  )

  const contextValue = useMemo(() => ({ showSelection, hasBeenProvided: true }), [showSelection])
  return (
    <DuplicateVinContext.Provider value={contextValue}>
      {state.newVehicle && (
        <VinAlreadyExistsModal
          data-testid='vin-already-exists-modal'
          open={!!state.newVehicle}
          previousVehicle={state.oldVehicle}
          onSubmit={state.resolve}
          onCancel={cancel}
          vin={state.oldVehicle?.Vin || ''}
          vehicle={state.newVehicle}
        />
      )}
      {children}
    </DuplicateVinContext.Provider>
  )
}

export const useShowDuplicateVinSelector = () => {
  const context = React.useContext(DuplicateVinContext)
  if (context === undefined || !context.hasBeenProvided) {
    console.error('useShowDuplicateVinSelector must be used within a DuplicateVinContext')
  }

  return context
}

export default DuplicateVin
